import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { SEOPageLayout, InlineCTA, FAQList } from "../../components/seo/SEOPageLayout";

const FAQS = [
  { q: "Can I sell unused diabetic test strips?", a: "Yes, you can sell unused, unexpired diabetic test strips in most cases. Boxes must be unopened, factory sealed, and not labeled Medicaid or Medicare. Contact us with a photo of your supplies and we will confirm whether they meet our current requirements." },
  { q: "How do I find out if my test strips are eligible?", a: "The fastest way is to text a photo of the front and back of the box to (904) 955-9646 or call us directly. We will review the brand, packaging, and expiration date and let you know if they qualify." },
  { q: "What condition do the test strips need to be in?", a: "Boxes must be sealed, in good physical condition, with intact packaging and no water damage. Damaged, opened, or expired boxes are not eligible." },
  { q: "How do I get started?", a: "Call or text (904) 955-9646, or use the Get Started form on our site. Share the brand, quantity, and expiration date, and we will walk you through the next step." },
  { q: "Do you serve Jacksonville, Florida?", a: "Yes. We serve Jacksonville and the surrounding Northeast Florida area, including Jacksonville Beach, Orange Park, St. Augustine, Fernandina Beach, and nearby communities." },
];

const ACCEPTED_BRANDS = [
  { group: "Continuous Glucose Monitors", items: ["Dexcom G7 sensors and receivers", "Dexcom G6 sensors and transmitters", "FreeStyle Libre 3, Libre 3 Plus, Libre 2, and Libre 14-Day sensors and readers"] },
  { group: "Insulin Pumps and Pods", items: ["Omnipod 5, Omnipod DASH, and Omnipod Classic pods and PDMs", "Medtronic 780G, MiniMed, and Guardian 4 supplies", "Tandem t:slim pumps and Autosoft infusion sets"] },
  { group: "Test Strips", items: ["Accu-Chek (Aviva Plus, Guide, Smartview)", "OneTouch Verio and Ultra", "Contour Next", "ReliOn Prime", "True Metrix", "FreeStyle Lite"] },
  { group: "Insulin and Supplies", items: ["Humulin (N, R, 70/30)", "Novolin", "BD needles, lancets, reservoirs"] },
];

export default function SellTestStrips() {
  return (
    <SEOPageLayout
      title="Sell Diabetic Test Strips for Cash | Cash for Test Strips Jacksonville"
      description="Looking to sell eligible unused diabetic test strips? Contact Cash for Test Strips Jacksonville to learn about accepted supplies and get started."
      canonical="https://cashforteststripsjacksonville.com/sell-diabetic-test-strips/"
      breadcrumbLabel="Sell Diabetic Test Strips"
      primaryKeyword="Sell Diabetic Test Strips"
      h1="Sell Diabetic Test Strips for Cash"
      intro="If you have unopened, unexpired diabetic test strips gathering dust, we can help you find out if they are eligible for cash. Cash for Test Strips Jacksonville is a locally operated buyer serving Northeast Florida — no shipping, no waiting on checks, no third parties."
      faqs={FAQS}
    >
      {({ openForm }) => (
        <>
          <section data-testid="section-looking-to-sell">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
              Looking to Sell Unused Diabetic Test Strips?
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              This service is for people who have extra, unopened diabetic test strips they no longer need. Common reasons include a change in prescription, switching to a continuous glucose monitor, or a loved one who no longer uses that particular brand.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Whether or not a box is accepted depends on several factors, including the brand, the product type, the expiration date, the condition of the packaging, and current buying requirements. Not every box qualifies, so the first step is always to check eligibility before making the trip.
            </p>
          </section>

          <section data-testid="section-how-it-works">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>How the Process Works</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { n: "1", t: "Tell us what you have", d: "Send us a photo or list of the diabetic supplies you would like to sell, including the brand and expiration date." },
                { n: "2", t: "We review eligibility", d: "We review the information and confirm whether the supplies meet our current requirements or not." },
                { n: "3", t: "Complete the transaction", d: "If your supplies qualify, we walk you through the next steps to complete the transaction locally." },
              ].map((s) => (
                <div key={s.n} className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm" data-testid={`step-${s.n}`}>
                  <div className="w-10 h-10 bg-[#0F5132] text-white rounded-full flex items-center justify-center font-bold mb-3">{s.n}</div>
                  <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>{s.t}</h3>
                  <p className="text-[#4B5563]">{s.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section data-testid="section-accepted-brands">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>What Test Strips Do We Accept?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-6">
              We only accept brands we can verify and re-purpose responsibly. The current list of accepted diabetic supplies includes:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {ACCEPTED_BRANDS.map((cat) => (
                <div key={cat.group} className="bg-[#F5F5F0] rounded-xl p-5">
                  <h3 className="font-semibold text-[#0F5132] mb-3" style={{ fontFamily: "Manrope" }}>{cat.group}</h3>
                  <ul className="space-y-2">
                    {cat.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-[#4B5563]">
                        <CheckCircle className="w-4 h-4 text-[#0F5132] mt-1 flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#4B5563] mt-6">
              See the full <Link to="/accepted-diabetic-supplies/" className="text-[#0F5132] font-semibold underline">accepted diabetic supplies list</Link> for details on each brand.
            </p>
          </section>

          <section data-testid="section-requirements">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Requirements for Accepted Test Strips</h2>
            <ul className="space-y-3 text-lg text-[#4B5563]">
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span><strong>Unopened.</strong> Boxes must be factory sealed. Opened or partially used boxes are not eligible.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span><strong>Expiration date.</strong> Products must have at least six months remaining until the printed expiration date.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span><strong>Packaging condition.</strong> The outer box must be intact, without water damage, tears, or writing that obscures the barcode.</span></li>
              <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#0F5132] mt-1 flex-shrink-0" /><span><strong>Not labeled Medicaid or Medicare.</strong> Boxes bearing state or federal insurance markings are not eligible.</span></li>
            </ul>
          </section>

          <section data-testid="section-why">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Why Choose Cash for Test Strips Jacksonville?</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
              We are a locally operated buyer based in Jacksonville, FL. We answer the phone, respond to texts, and meet people in person — no mail-in delays, no anonymous middlemen. Every transaction happens face to face at a public location you choose.
            </p>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Eligibility is clear before you drive anywhere. If your supplies do not qualify, we tell you upfront so you do not waste time.
            </p>
          </section>

          <section data-testid="section-areas">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>Serving Jacksonville and Surrounding Areas</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              We regularly meet sellers in Jacksonville, Jacksonville Beach, Orange Park, Green Cove Springs, Clay County, St. Augustine, Fernandina Beach, Amelia Island, and Yulee. If you are roughly within 45 minutes of downtown Jacksonville, we can typically come to a public location convenient for you.
            </p>
          </section>

          <section data-testid="section-faq">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }}>Frequently Asked Questions</h2>
            <FAQList faqs={FAQS} />
          </section>

          <InlineCTA
            onGetStarted={openForm}
            headline="Find Out if Your Supplies Are Eligible"
            sub="Text a photo to (904) 955-9646 or use the form. We will let you know quickly."
          />
        </>
      )}
    </SEOPageLayout>
  );
}
